import { useEffect, useState } from 'react';
import { hairdresserService } from '../../../_services/hairdresser.service';
import { serviceLine } from '../../DiaryV2/serviceLine';
import { toE164 } from './api';
import { firstName } from './needs';

/**
 * Turns the booking Bailey worked out with a client into the diary's own
 * new-booking panel, filled in: the stylist's services by id, the day and
 * time, and the client, found by their email or number, or their details
 * ready in the add-client form when they're new. The owner checks it and
 * saves it there; nothing is booked from here.
 */

const digits = (s) => String(s || '').replace(/\D/g, '');
// The last ten digits: 07700 900284 and +447700900284 are one number.
const tail = (s) => digits(s).slice(-10);
const pad = (n) => String(n).padStart(2, '0');
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

async function servicesOf(staffId) {
    const res = await hairdresserService.getServicesGroupedV2({ hairdresserId: staffId });
    const cats = (res && res.data && res.data.data && res.data.data.hairdresserSelectedServices) || [];
    return cats.reduce((all, c) => all.concat(c.hairdresserService || c.services || []), []);
}

// Service names by stylist, asked for once a visit, for the bookings shown
// on cards. Getting a booking ready always asks afresh.
const named = new Map();
function namesFor(staffId) {
    const k = `${localStorage.getItem('salonId') || ''}:${staffId}`;
    if (!named.has(k)) {
        named.set(k, servicesOf(staffId)
            .then((all) => new Map(all.map((s) => [String(s._id || s.serviceId), s.serviceName])))
            .catch((e) => { named.delete(k); throw e; }));
    }
    return named.get(k);
}

/**
 * The booking with its services named. The conversation doesn't always
 * have the names (a long service list is cut short when it's saved), so
 * they're asked for from the stylist's own list when missing.
 */
export function useNamedBooking(booking) {
    const missing = !!booking && (booking.services || []).length < booking.serviceIds.length;
    const key = booking ? `${booking.staffId}|${booking.serviceIds.join(',')}` : '';
    const [found, setFound] = useState(null);
    useEffect(() => {
        if (!missing) return undefined;
        let live = true;
        namesFor(booking.staffId).then((byId) => {
            const names = booking.serviceIds.map((id) => byId.get(String(id))).filter(Boolean);
            if (live && names.length) setFound({ key, names });
        }).catch(() => { /* the card says "An appointment" */ });
        return () => { live = false; };
    }, [missing, key]); // eslint-disable-line react-hooks/exhaustive-deps
    return missing && found && found.key === key ? { ...booking, services: found.names } : booking;
}

async function searchClients(searchKey) {
    const res = await hairdresserService.getClientsPaginated({ searchKey, page: 1, limit: 20, sortField: 'name', sortOrder: 'asc' });
    const d = (res && res.data && (res.data.responseData || res.data.data)) || {};
    const rows = d.clients || d.clientList || [];
    return Array.isArray(rows) ? rows : [];
}

// The same first name, or no name to go on. A number shared by a family
// mustn't book the booking under someone else.
const sameName = (a, b) => !firstName(a) || !firstName(b) || firstName(a).toLowerCase() === firstName(b).toLowerCase();

/** The salon's client with this email or number, or null when there isn't one. */
export async function findClient({ name, phone, email }) {
    if (email) {
        const rows = await searchClients(email);
        const hit = rows.find((c) => String(c.email || '').trim().toLowerCase() === email.toLowerCase() && sameName(c.name, name));
        if (hit) return hit;
    }
    if (tail(phone).length >= 9) {
        const rows = await searchClients(tail(phone));
        const hit = rows.find((c) => tail(c.phoneNumber) === tail(phone) && sameName(c.name, name));
        if (hit) return hit;
    }
    return null;
}

/** "Sarah Jennings" as the add-client form's fields. */
export function newClientFrom({ name, phone, email }) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    return {
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' '),
        email: email || '',
        phoneNumber: toE164(phone) || ''
    };
}

const asClient = (c) => ({ _id: c._id, name: c.name, email: c.email || '', phoneNumber: c.phoneNumber || '' });

async function clientFor(person) {
    const found = await findClient(person).catch(() => null);
    return found ? { client: asClient(found), newClient: null } : { client: null, newClient: newClientFrom(person) };
}

/** The booking, ready for the diary's panel. `missing` counts services the stylist no longer has. */
export async function prepareBooking(booking, person) {
    const [all, who] = await Promise.all([servicesOf(booking.staffId), clientFor(person)]);
    const byId = new Map(all.map((s) => [String(s._id || s.serviceId), s]));
    const picked = booking.serviceIds.map((id) => byId.get(String(id))).filter(Boolean);
    const [h, m] = String(booking.slot).split(':').map(Number);
    return {
        type: 'booking',
        staffId: booking.staffId,
        bookingDate: booking.date,
        startMinutes: h * 60 + m,
        ...who,
        services: picked.map((s) => serviceLine(s, booking.staffId)),
        missing: booking.serviceIds.length - picked.length
    };
}

/**
 * The consultation or patch test a booking needs first, with the same
 * stylist, for today with no time yet, so the owner picks one. Null when the
 * stylist has no such service.
 */
export async function prepareConsultation(booking, person) {
    const [all, who] = await Promise.all([servicesOf(booking.staffId), clientFor(person)]);
    const byId = new Map(all.map((s) => [String(s._id || s.serviceId), s]));
    const needs = booking.serviceIds.map((id) => byId.get(String(id))).find((s) => s && s.requiresConsultation && s.consultationServiceId);
    const cid = needs && String(needs.consultationServiceId);
    const consult = cid && all.find((s) => String(s.commonServiceId) === cid || String(s._id) === cid);
    if (!consult) return null;
    return {
        type: 'booking',
        staffId: booking.staffId,
        bookingDate: today(),
        startMinutes: null,
        ...who,
        services: [serviceLine(consult, booking.staffId)],
        missing: 0
    };
}

/** "Book the patch test first" or "Book a consultation first", in the salon's own word. */
export const consultLabel = (booking) => (/patch/i.test(String((booking && booking.rule) || '')) ? 'Book the patch test first' : 'Book a consultation first');
